/**
 * src/state/realtimeSignals.js
 *
 * In-page fan-out for the ONE realtime connection usePersistence owns
 * (src/api/realtimeClient.js). Screens that need to react to a specific kind
 * of server change — the risk register (URRA-05) — subscribe here instead of
 * opening a second socket, and read whether the socket is live so they can
 * fall back to polling when it is not.
 *
 * Status values:
 *   'connecting'   — no socket yet (initial state, or between reconnects)
 *   'connected'    — socket open; server signals are being delivered
 *   'unavailable'  — negotiate refused (503: no Web PubSub in this environment,
 *                    401/403: no usable session) — will not recover by itself
 *   'disconnected' — socket closed or negotiate failed; reconnect scheduled
 *
 * Only 'connected' means signals are arriving. Every other value means a
 * subscriber must keep its own fallback refresh running.
 */

let status = 'connecting';
const statusListeners = new Set();
const signalListeners = new Set();

export function getRealtimeStatus() {
  return status;
}

export function isRealtimeConnected() {
  return status === 'connected';
}

export function setRealtimeStatus(next) {
  if (next === status) return;
  status = next;
  for (const fn of [...statusListeners]) {
    try {
      fn(status);
    } catch {
      // A broken listener must not stop the others hearing the change.
    }
  }
}

/** @returns {() => void} unsubscribe */
export function subscribeRealtimeStatus(fn) {
  statusListeners.add(fn);
  return () => statusListeners.delete(fn);
}

export function emitRealtimeSignal(signal) {
  for (const fn of [...signalListeners]) {
    try {
      fn(signal);
    } catch {
      // Same isolation as above.
    }
  }
}

/** @returns {() => void} unsubscribe */
export function subscribeRealtimeSignal(fn) {
  signalListeners.add(fn);
  return () => signalListeners.delete(fn);
}

/**
 * True when a state-changed signal concerns any of `keys`. A signal that names
 * no keys is treated as "anything may have changed" — refreshing on it costs a
 * read, skipping it could leave a screen stale.
 */
export function signalTouches(signal, keys) {
  if (!signal || signal.type !== 'state-changed') return false;
  const changed = signal.changedKeys;
  if (!Array.isArray(changed) || changed.length === 0) return true;
  return changed.some((k) => keys.includes(k));
}

// Test seam.
export function _resetRealtimeSignalsForTests() {
  status = 'connecting';
  statusListeners.clear();
  signalListeners.clear();
}
